import { Component, OnInit, ViewChild } from '@angular/core';
import { CoreService } from '@reenstudio/app/core/services/core.service';

import {
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexLegend,
  ApexStroke,
  ApexTooltip,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexGrid,
  ApexPlotOptions,
  ApexFill,
  ApexMarkers,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { DecimalPipe, NgForOf, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TablerIconsModule } from 'angular-tabler-icons';
import { EventService } from '@reenstudio/app/core/services/event-service';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { NGXLogger } from 'ngx-logger';
import { UserRole } from '@reenstudio/app/core/enum/user-role';
import { historyWidgetsChartObj } from '@reenstudio/app/core/models/charts/history-widgets';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { PeriodChartApiService } from '@reenstudio/app/core/services/charts-api/period-api-service';
import { SiteService } from '@reenstudio/app/core/services/site-service';

export interface breadcrumbOption {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  legend: ApexLegend;
  grid: ApexGrid;
  marker: ApexMarkers;
}

@Component({
  selector: 'app-widgets',
  templateUrl: './widgets.component.html',
  styleUrls: ['./widgets.component.scss'],
  standalone: true,
  imports: [
    NgApexchartsModule,
    NgIf,
    NgForOf,
    MatCardModule,
    MatProgressBarModule,
    NgxSkeletonLoaderModule,
    TablerIconsModule,
    DecimalPipe,
  ],
})
export class WidgetsComponent implements OnInit {
  @ViewChild('chart') chart: ChartComponent = Object.create(null);

  public breadcrumbOption!: Partial<breadcrumbOption> | any;
  public breadcrumb2Option!: Partial<breadcrumbOption> | any;

  options = this.settings.getOptions();

  loading: boolean = false;
  powerData: {
    prodotta: string;
    consumata: string;
    co2: string;
    pm10: string;
  } = {
    prodotta: '0',
    consumata: '0',
    co2: '0',
    pm10: '0',
  };

  selectedCer: number;
  wrongFilters: boolean = false;

  chartObj: historyWidgetsChartObj = {
    startPeriod: 1, // Default 1 giorno prima
    type: 'days',
    end: new Date(),
    start: new Date(),
    year: 0,
    month: 0,
    selectedSite: 'total',
  };

  constructor(
    private settings: CoreService,
    private apiService: PeriodChartApiService,
    private siteService: SiteService,
    private storageService: StorageService,
    private logger: NGXLogger
  ) {
    this.chartObj.start = new Date(
      this.chartObj.end.getTime() -
        this.chartObj.startPeriod * 24 * 60 * 60 * 1000
    );

    this.breadcrumbOption = {
      series: [
        {
          name: '',
          data: [1.1, 1.4, 1.1, 0.9, 1.9, 1, 0.3, 1.1],
        },
      ],
      chart: {
        type: 'bar',
        height: 35,
        width: 60,
        fontFamily: 'Poppins,sans-serif',
        sparkline: {
          enabled: true,
        },
      },
      grid: {
        borderColor: 'rgba(0,0,0,.2)',
        strokeDashArray: 3,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
      },

      legend: {
        show: false,
      },
      fill: {
        colors: ['#1e88e5'],
        opacity: 1,
      },
      tooltip: {
        enabled: false,
      },
    };

    this.breadcrumb2Option = {
      series: [
        {
          name: '',
          data: [1.1, 1.4, 1.1, 0.9, 1.9, 1, 0.3, 1.1],
        },
      ],
      chart: {
        type: 'bar',
        height: 35,
        width: 60,
        fontFamily: 'Poppins,sans-serif',
        sparkline: {
          enabled: true,
        },
      },
      grid: {
        borderColor: 'rgba(0,0,0,.2)',
        strokeDashArray: 3,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
      },

      legend: {
        show: false,
      },
      fill: {
        colors: ['#26c6da'],
        opacity: 1,
      },
      tooltip: {
        enabled: false,
      },
    };
  }

  ngOnInit() {
    const cer = this.storageService.getCer();
    if (cer) {
      this.selectedCer = cer;
    }
    this.getData();

    EventService.pageFilters.subscribe((data) => {
      this.logger.debug('Widget component filter event data:', data);
      this.chartObj.type = data.periodType;

      if (this.chartObj.type === 'days') {
        this.chartObj.startPeriod =
          data.periodValue > 1
            ? Number.parseInt(data.periodValue) - 1
            : Number.parseInt(data.periodValue);
        this.chartObj.end = new Date();
        this.chartObj.start = new Date(
          this.chartObj.end.getTime() -
            this.chartObj.startPeriod * 24 * 60 * 60 * 1000
        );
      } else if (this.chartObj.type === 'custom') {
        this.chartObj.start = data.customStartDate;
        this.chartObj.end = data.customEndDate;

        const differenzaInMillisecondi =
          this.chartObj.end.getTime() - this.chartObj.start.getTime();
        // Conteggio giorni per settare a true ingoreTime
        if (differenzaInMillisecondi / (1000 * 60 * 60 * 24) > 1) {
          this.chartObj.startPeriod = Number.parseInt(
            (differenzaInMillisecondi / (1000 * 60 * 60 * 24)).toFixed(0)
          );
        }
      } else if (this.chartObj.type === 'months') {
        this.chartObj.year = data.yearsList;
        this.chartObj.month = data.monthList - 1;

        const lastDayOfMonth = this.getTotalDaysInMonth(this.chartObj);

        this.chartObj.start = new Date(
          this.chartObj.year,
          this.chartObj.month,
          1
        );
        this.chartObj.end = new Date(
          this.chartObj.year,
          this.chartObj.month,
          lastDayOfMonth
        );
      }

      // Se il livello è site, il valore è quello salvato sullo storage
      if (this.storageService.getLevel() === UserRole.SITE) {
        this.siteService.setSelectedSite().subscribe((selectedSite) => {
          this.chartObj.selectedSite = selectedSite.id;
        });
      } else {
        //Diversamente è quello selezionato dall'utente
        this.chartObj.selectedSite = data.site;
      }

      this.getData();
    });

    if (this.storageService.getLevel() === UserRole.SITE) {
      this.siteService.setSelectedSite().subscribe((selectedSite) => {
        this.chartObj.selectedSite = selectedSite.id;
      });
    }

    this.getData();

    EventService.changeCer.subscribe(() => {
      const cer = this.storageService.getCer();
      if (cer) {
        this.selectedCer = cer;
      }
      this.getData();
    });

    EventService.changeSite.subscribe(() => {
      this.siteService.setSelectedSite().subscribe((selectedSite) => {
        this.chartObj.selectedSite = selectedSite.id;
      });

      this.getData();
    });
  }

  getData() {
    this.loading = true;
    let ignoreTime = false;

    if (this.selectedCer && this.chartObj.start) {
      if (this.chartObj.type === 'custom') {
        ignoreTime = true;
      }

      this.logger.debug('Widgets IgnoreTime value', ignoreTime);
      this.logger.debug('Widgets chartObj data', this.chartObj);
      this.logger.debug('Widgets selectedCer value', this.selectedCer);

      if (this.storageService.getLevel() === UserRole.SITE) {
        this.siteService.setSelectedSite().subscribe((selectedSite: any) => {
          this.chartObj.selectedSite = selectedSite.id;
        });
      }

      this.apiService
        .getPeriod(
          this.selectedCer,
          this.chartObj.selectedSite,
          this.chartObj.start,
          this.chartObj.end,
          ignoreTime
        )
        .subscribe({
          next: (data) => {
            this.logger.debug('Widgets API received data', data);
            let prodotto = 0;
            let consumato = 0;

            for (let value of data) {
              prodotto += Number.parseInt(value.product);
              consumato += Number.parseInt(value.consumption);
            }

            this.powerData.consumata = (consumato / 1000).toFixed(2);
            this.powerData.prodotta = (prodotto / 1000).toFixed(2);
            this.powerData.co2 = ((0.53 * prodotto) / 1000).toFixed(2);
            this.powerData.pm10 = ((1.46 * prodotto) / 1000).toFixed(2);

            this.powerData.consumata = this.powerData.consumata.replace(
              '.',
              ','
            );
            this.powerData.prodotta = this.powerData.prodotta.replace('.', ',');
            this.powerData.co2 = this.powerData.co2.replace('.', ',');
          },
          error: (errore) => {
            console.error('Errore durante la chiamata API:', errore);
          },
          complete: () => {
            this.loading = false;
          },
        });
    } else {
      this.loading = false;
      this.wrongFilters = true;
    }
  }

  getTotalDaysInMonth(chartObj: historyWidgetsChartObj): number {
    // month è 0-based, quindi sottrai 1
    const lastDayOfMonth = new Date(chartObj.year, chartObj.month + 1, 0);
    return lastDayOfMonth.getDate();
  }
}
